class BannerParallax {
  constructor(sectionId) {
    this.banner = null;
    this.bannerContainer = null;
    this.isActive = false;
    this.ticking = false;
    this.scrollY = 0;
    this.parallaxSpeed = 0.2; // Adjust this value (0.1 to 1.0) for different parallax speeds
    this.banner = document.querySelector(`${sectionId} .banner__image`);
    this.bannerContainer = document.querySelector(`${sectionId} .banner__image-container`);

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupParallax());
    } else {
      this.setupParallax();
    }
  }

  setupParallax() {
    if (!this.banner || !this.bannerContainer) {
      console.warn('Banner parallax: Required elements not found');
      return;
    }

    // Set initial styles for better parallax effect
    this.setupStyles();

    // Use Intersection Observer for performance optimization
    this.setupIntersectionObserver();

    // Handle window resize
    window.addEventListener(
      'resize',
      this.throttle(() => {
        if (this.isActive) {
          this.updateParallax();
        }
      }, 100)
    );
  }

  setupStyles() {
    const bannerRect = this.banner.getBoundingClientRect();

    // Ensure the banner image is larger than its container for parallax effect
    this.banner.style.height = '150%';
    this.banner.style.objectFit = 'cover';
    this.banner.style.objectPosition = 'center';
    this.banner.style.willChange = 'transform';
    this.banner.style.transform = `translate3d(0, ${bannerRect.height * -0.5}px, 0)`; // Enable hardware acceleration

    // Ensure container clips overflow
    this.bannerContainer.style.overflow = 'hidden';
  }

  setupIntersectionObserver() {
    // Only activate parallax when banner is near viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            this.activateParallax();
          } else {
            this.deactivateParallax();
          }
        });
      },
      {
        rootMargin: '100px 0px 100px 0px', // Start parallax before element enters viewport
      }
    );

    observer.observe(this.bannerContainer);
  }

  activateParallax() {
    if (this.isActive) return;

    this.isActive = true;
    this.scrollY = window.pageYOffset;
    this.addScrollListener();
    this.updateParallax(); // Initial update
  }

  deactivateParallax() {
    if (!this.isActive) return;

    this.isActive = false;
    this.removeScrollListener();
  }

  addScrollListener() {
    window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
  }

  removeScrollListener() {
    window.removeEventListener('scroll', this.onScroll.bind(this));
  }

  onScroll() {
    this.scrollY = window.pageYOffset;
    this.requestTick();
  }

  requestTick() {
    if (!this.ticking) {
      requestAnimationFrame(this.updateParallax.bind(this));
      this.ticking = true;
    }
  }

  updateParallax() {
    this.ticking = false;

    if (!this.isActive || !this.banner || !this.bannerContainer) return;

    // Get banner position relative to viewport
    const bannerRect = this.bannerContainer.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Calculate parallax offset
    // The banner moves slower than the scroll speed
    const bannerCenter = bannerRect.top + bannerRect.height / 1.5;
    const windowCenter = windowHeight / 2;
    const offsetFromCenter = bannerCenter - windowCenter;

    // Apply parallax transformation (negated for top-to-bottom effect)
    const parallaxOffset = -(offsetFromCenter * this.parallaxSpeed);
    const transform = `translate3d(0, ${parallaxOffset}px, 0)`;

    this.banner.style.transform = transform;
  }

  // Utility function to throttle events
  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  // Public method to change parallax speed
  setParallaxSpeed(speed) {
    this.parallaxSpeed = Math.max(0.1, Math.min(1.0, speed));
    if (this.isActive) {
      this.updateParallax();
    }
  }

  // Public method to destroy the parallax effect
  destroy() {
    this.deactivateParallax();
    if (this.banner) {
      this.banner.style.transform = '';
      this.banner.style.willChange = '';
    }
  }
}

// Make it available globally for manual control
window.BannerParallax = BannerParallax;
